<template>
  <div class="domain-trend-funnel" :class="{ 'no-data': !listData.length }">
    <button class="more-button" @click="onMoreButtonClick">
      <span>{{ $t('dashboard.analysis.trend.moreTrends') }}</span>
      <i class="icon icon--arrow-right-red size-13"></i>
    </button>
    <template v-if="listData.length">
      <app-chart
        chartType="line"
        :chartOptions="chartOptions"
        :height="128"
        :useResize="true"
      >
      </app-chart>
      <div class="info">
        <div class="seq" v-for="(item, index) in listDataList" :key="index">
          <div class="seq-info">
            <p class="seq-info-title">{{ index + 1 }}seq</p>
            <div class="seq-info-session">
              <i class="icon icon--ba-session-small-new"></i>
              <p>{{ item.seqSessCnt | thousand }}</p>
            </div>
            <div class="conversion-rate" v-if="index !== listDataList.length - 1">
              {{ item.conversionRate }}%
              <i class="icon icon--ranking-stayInCount size-20"></i>
            </div>
          </div>
          <div class="seq-rank1">
            <div class="seq-rank1-label" v-if="index === 0">
              <p>{{ $t('dashboard.analysis.trend.rankBySequence') }}</p>
              <i class="icon icon--arrow-dash"></i>
            </div>
            <p class="seq-rank1-url" :title="item.rank1Url">{{ item.rank1Url }}</p>
            <p class="seq-rank1-session">{{ item.rank1Session | letterThousandMillion(0) }} Session ({{ item.rank1Ratio }}%)</p>
          </div>
        </div>
      </div>
    </template>
    <p class="no-data" v-else>{{ $t('dashboard.analysis.trend.noData') }}</p>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { simpleUrl } from '@/libs/utils'
import AppChart from '@/components/chart'
export default {
  name: 'DomainTrendFunnel',
  components: {
    AppChart
  },
  props: {
    listData: {
      type: Array,
      default: () => { return [] }
    }
  },
  computed: {
    ...mapState([
      'domain',
      'userData',
      'journeyOptions',
      'journeyPeriod'
    ]),
    listDataList () {
      return this.listData.map((item, index) => {
        const data = {
          ...item,
          rank1Url: item.rank1Url,
          rank1Session: item.rank1Session
        }
        const currentSeqSessCnt = item.seqSessCnt || 0
        if (index !== this.listData.length - 1) {
          const nextSeq = this.listData[index + 1]
          const nextSeqSessCnt = nextSeq && nextSeq.seqSessCnt ? nextSeq.seqSessCnt : 0
          const rate = (nextSeqSessCnt && currentSeqSessCnt) ? (nextSeqSessCnt / currentSeqSessCnt) * 100 : 0
          data.conversionRate = parseFloat(rate.toFixed(1))
        }
        if (item.rank1Node && item.rank1Node.nodeMeta && item.rank1Node.nodeMeta.url) {
          data.rank1Url = simpleUrl(item.rank1Node.nodeMeta.url, this.domain.matchType, this.domain.matchType !== 'simple')
          data.rank1Session = item.rank1Node.sessCnt
          const ratio = (item.rank1Node.sessCnt && currentSeqSessCnt) ? (item.rank1Node.sessCnt / currentSeqSessCnt) * 100 : 0
          data.rank1Ratio = parseFloat(ratio.toFixed(1))
        } else {
          data.rank1Url = '-'
          data.rank1Session = 0
          data.rank1Ratio = 0
        }
        return data
      })
    },
    chartOptions () {
      const seriesData = this.listDataList.map(item => item.seqSessCnt)
      if (seriesData.length === 6) {
        seriesData.push(seriesData[seriesData.length - 1])
      }
      return {
        series: [
          {
            data: this.listDataList.map(item => item.seqSessCnt),
            type: 'line',
            areaStyle: {},
            lineStyle: { width: 2 },
            smooth: true,
            showSymbol: false
          }
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          axisLabel: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        yAxis: {
          axisLabel: {
            show: false
          },
          splitLine: {
            show: false
          }
        },
        legend: { show: false },
        grid: {
          top: 1,
          left: 0,
          right: 0,
          bottom: 1
        },
        tooltip: {
          trigger: 'axis'
        },
        areaStyle: {
          color: 'rgba(17, 153, 175, 0.3)'
        },
        lineStyle: {
          color: '#0E90A5'
        }
      }
    }
  },
  methods: {
    onMoreButtonClick () {
      const queryData = {
        sid: this.domain.orgSid || this.domain.sid
      }

      if (this.domain.shared === 'shared') {
        queryData.subSid = this.domain.subDomains[0].orgSid
        queryData.path = null
      }

      if (this.userData.userId === 'samsung_gmc') {
        queryData.subSid = this.domain.subDomains.find(subDomain => subDomain.domain === 'samsung.com').orgSid
        queryData.path = 'uk'
      }

      let routeData = this.$router.resolve({
        name: 'trend',
        query: queryData
      })

      const newWindow = window.open(routeData.href, '_blank')
      newWindow._journeyOptionsFromParent = { ...this.journeyOptions }
      newWindow._journeyPeriodFromParent = { ...this.journeyPeriod }
    }
  }
}
</script>

<style lang="scss" scoped>
.domain-trend-funnel {
  position: relative;
  height: 100%;
  .more-button {
    position: absolute;
    right: -10px;
    top: -9px;
    transform: translateY(-100%);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    padding: 10px;
    color: #444;
    font-size: 13px;
    font-weight: 600;
    &:hover {
      color: #777;
    }
  }
  /deep/ .chart_container {
    position: absolute;
    top: 77px;
  }
  .info {
    display: flex;
    height: 100%;
    .seq {
      position: relative;
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 8px 8px 4px;
      &:last-child {
        &::after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          width: 1px;
          height: 100%;
          background-color: #BBB;
        }
      }
      .seq-info {
        display: flex;
        flex-direction: column;
        gap: 12px;
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 1px;
          height: 100%;
          background-color: #BBB;
        }
        .seq-info-title {
          color: #444;
          font-size: 20px;
          font-weight: 600;
        }
        .seq-info-session {
          @include flexYcenter;
          gap: 4px;
          color: #0E90A5;
          font-size: 16px;
          font-weight: 600;
        }
        .conversion-rate {
          position: absolute;
          right: 4px;
          top: 7px;
          @include flexYcenter;
          gap: 4px;
          color: #9AAF46;
          font-size: 12px;
          font-weight: 600;
        }
      }
      .seq-rank1 {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
        .seq-rank1-label {
          @include flexYcenter;
          gap: 16px;
          color: #444;
          font-size: 13px;
          font-weight: 600;
        }
        .seq-rank1-url {
          width: 100%;
          min-height: 28px;
          display: -webkit-box;
          line-clamp: 2;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-all;
          color: #444;
          font-size: 11px;
          font-weight: 400;
          line-height: 120%;
        }
        .seq-rank1-session {
          color: #0E90A5;
          font-size: 12px;
          font-weight: 600;
        }
      }
    }
  }
  .no-data {
    padding-top: 126px;
    color: #999;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
  }
  &.no-data {
    pointer-events: none;
    .more-button {
      opacity: 0.3;
    }
  }
}
</style>
